import type { ComponentType, ReactNode, SVGProps } from 'react';
import { ChevronRightIcon } from '@heroicons/react/24/outline';

interface AccessCardProps {
    /** Both optional so a card can render headerless (shell + body only) - e.g. RolesList's tab bar. */
    icon?: ComponentType<SVGProps<SVGSVGElement>>;
    title?: ReactNode;
    /** Header count/meta slot after the title; omitted entirely when undefined. */
    count?: ReactNode;
    /** Header action slot, right-aligned. */
    action?: ReactNode;
    /** Omitted/nullish skips the body wrapper entirely (header-only card). */
    children?: ReactNode;
    /** Full override for the body's className, when neither default padding nor `noBodyPadding` fits. */
    bodyClassName?: string;
    /** Drops body padding so a table/edge-to-edge row list reaches the card's own borders. */
    noBodyPadding?: boolean;
    /** Card grows to fill its flex column instead of sizing to content. Default false. */
    grow?: boolean;
    /** Toggle button with rotating chevron gating the body behind `open`; `action` stays a sibling. */
    collapsible?: boolean;
    /** Only meaningful when `collapsible` is set. Defaults to true (expanded). */
    open?: boolean;
    onToggle?: () => void;
}

// The Access section's one card primitive - same visual language as the
// practice Details tab's DetailGroupCard, used by every Access screen.
export function AccessCard({
    icon: Icon,
    title,
    count,
    action,
    children,
    bodyClassName,
    noBodyPadding = false,
    grow = false,
    collapsible = false,
    open = true,
    onToggle
}: AccessCardProps) {
    const bodyClass =
        bodyClassName ??
        `flex flex-col ${grow ? 'flex-1 min-h-0' : ''} ${noBodyPadding ? '' : 'p-5 gap-5'}`.trim();
    const showBody = (!collapsible || open) && children != null;
    // An icon-less, title-less card drops the header row entirely rather than rendering it empty.
    const hasHeader = Boolean(Icon) || title != null;

    const headerLabel = (
        <>
            {Icon && <Icon className="h-4 w-4 text-gray-400 shrink-0" />}
            {title != null && (
                <h3 className="text-[13px] font-bold text-gray-800 truncate min-w-0">{title}</h3>
            )}
            {count !== undefined && (
                <span className="text-[12px] text-gray-400 shrink-0">{count}</span>
            )}
        </>
    );

    return (
        <div
            className={`bg-white border border-gray-200 rounded-xl shadow-[0px_1px_2px_rgba(0,0,0,0.06)] overflow-hidden ${
                grow ? 'flex-1 min-h-0 flex flex-col' : 'shrink-0'
            }`}
        >
            {hasHeader && (
                <div className="border-b border-gray-100 flex items-center gap-2.5 px-5 h-12 shrink-0">
                    {collapsible ? (
                        <button
                            type="button"
                            onClick={onToggle}
                            aria-expanded={open}
                            className="flex items-center gap-2.5 flex-1 min-w-0 h-full text-left"
                        >
                            <ChevronRightIcon
                                className={`h-3.5 w-3.5 text-gray-400 shrink-0 transition-transform duration-150 ${
                                    open ? 'rotate-90' : ''
                                }`}
                            />
                            {headerLabel}
                        </button>
                    ) : (
                        headerLabel
                    )}
                    {action && <span className="ml-auto shrink-0">{action}</span>}
                </div>
            )}
            {showBody && <div className={bodyClass}>{children}</div>}
        </div>
    );
}
